import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button, Label, MultiSelect } from '@parrot-software/design-system';
import { Search, Plus } from '@parrot-software/design-system/icons';
import { useHistory } from 'react-router-dom';
import type { Role } from '@personnel/domain/entities';
import { VerificationStatus } from '@personnel/domain/types';
import type { EmployeeFiltersStorage } from '@personnel/application/storage/types';

export interface Store {
  uuid: string;
  name: string;
  isDraft: boolean;
}

interface EmployeesSearchBarProps {
  filters: EmployeeFiltersStorage;
  onFiltersChange: (filters: EmployeeFiltersStorage) => void;
  stores: Store[];
  roles: Role[];
}

/**
 * Employees Search Bar Component
 *
 * Search input and create button for employees list.
 * Uses Semantic Styles Pattern for maintainability.
 *
 * @param {EmployeesSearchBarProps} props - Component properties
 * @returns {React.ReactElement} Employees search bar component
 */
const styles = {
  container: 'w-full',
  searchWrapper: 'flex flex-wrap items-end gap-4',
  searchContainer: 'relative flex grow basis-[220px] flex-col gap-2',
  searchIcon: 'absolute right-3 top-[65%] -translate-y-1/2 size-4 text-muted-foreground',
  buttonIcon: 'size-4',
  buttonSearch: 'ml-auto h-10 w-full sm:w-fit',
};

export const EmployeesSearchBar = ({
  filters,
  onFiltersChange,
  stores,
  roles,
}: EmployeesSearchBarProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const history = useHistory();
  const [inputValue, setInputValue] = useState(filters.user);

  useEffect(() => {
    if (filters.user !== inputValue) {
      setInputValue(filters.user);
    }
  }, [filters.user]);

  useEffect(() => {
    if (inputValue === filters.user) return;

    const handler = setTimeout(() => {
      onFiltersChange({ ...filters, user: inputValue });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleStoreChange = (values: string[]) => {
    onFiltersChange({ ...filters, store: values });
  };

  const handleRoleChange = (values: string[]) => {
    onFiltersChange({ ...filters, role: values });
  };

  const handleStatusChange = (values: string[]) => {
    onFiltersChange({ ...filters, status: values });
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleCreateClick = () => {
    history.push('/employees/create');
  };

  return (
    <div className={styles.container}>
      {/* Search Input */}
      <div className={styles.searchWrapper}>
        <div className={styles.searchContainer}>
          <Label>{t('searchBar.storeLabel')}</Label>
          <MultiSelect
            className="[&_svg]:w-3! [&_svg]:h-3!"
            clearLabel={t('list.clear')}
            closeLabel={t('list.close')}
            defaultValue={filters.store}
            maxCount={2}
            onValueChange={handleStoreChange}
            options={[...stores.map((store) => ({ label: store.name, value: store.uuid }))]}
            placeholder={t('searchBar.storePlaceholder')}
            searchable
            selectAllLabel={t('searchBar.allStores')}
            singleLine
            value={filters.store}
          />
        </div>
        <div className={styles.searchContainer}>
          <Label>{t('searchBar.roleLabel')}</Label>
          <MultiSelect
            className="[&_svg]:w-3! [&_svg]:h-3!"
            clearLabel={t('list.clear')}
            closeLabel={t('list.close')}
            defaultValue={filters.role}
            maxCount={2}
            onValueChange={handleRoleChange}
            options={roles.map((r) => ({ label: r.name, value: r.uuid }))}
            placeholder={t('searchBar.rolesPlaceholder')}
            searchable
            selectAllLabel={t('searchBar.allRoles')}
            singleLine
            value={filters.role}
          />
        </div>
        <div className={styles.searchContainer}>
          <Label>{t('searchBar.statusLabel')}</Label>
          {/* Not searchable: two options are read, not searched. */}
          <MultiSelect
            className="[&_svg]:w-3! [&_svg]:h-3!"
            clearLabel={t('list.clear')}
            closeLabel={t('list.close')}
            defaultValue={filters.status}
            maxCount={2}
            onValueChange={handleStatusChange}
            options={[
              { label: t('verificationBadge.verified'), value: VerificationStatus.VERIFIED },
              { label: t('verificationBadge.unverified'), value: VerificationStatus.NOT_VERIFIED },
            ]}
            placeholder={t('searchBar.statusPlaceholder')}
            searchable={false}
            selectAllLabel={t('searchBar.allStatuses')}
            singleLine
            value={filters.status}
          />
        </div>
        <div className={styles.searchContainer}>
          <Label>{t('searchBar.userLabel')}</Label>
          <Input
            className="min-h-10"
            onChange={handleUserChange}
            placeholder={t('searchBar.userPlaceholder')}
            type="text"
            value={inputValue}
          />
          <Search className={styles.searchIcon} />
        </div>
        {/* Create Button */}
        <Button className={styles.buttonSearch} onClick={handleCreateClick} type="button" variant="default">
          <Plus className={styles.buttonIcon} />
          {t('list.createButton')}
        </Button>
      </div>
    </div>
  );
};
